import { useQuery } from "@apollo/client";
import {
  GetOverviewFavouriteGoalsQueryQuery,
  GetOverviewFavouriteGoalsQueryQueryVariables,
  GoalOverviewGoalFragmentFragment,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";
import Loading from "@components/loading/loading";
import {
  UserComboboxOption,
  UserComboboxOptionType,
} from "@components/user-combobox/user-combobox-list";
import { assertEdgesNonNull } from "@helpers/helpers";

import GoalOverviewGoalTable from "./goal-table";
import getOverviewFavouriteGoalsQuery from "./graphql/get-overview-favourite-goals-query";

const GoalOverviewFavouriteGoals = ({
  selected,
  goalDueBetweenDates,
}: {
  selected: UserComboboxOption;
  goalDueBetweenDates: string[];
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const isCurrentUser =
    selected.type === UserComboboxOptionType.USER &&
    selected.id === currentUser.id;

  const { data, loading } = useQuery<
    GetOverviewFavouriteGoalsQueryQuery,
    GetOverviewFavouriteGoalsQueryQueryVariables
  >(getOverviewFavouriteGoalsQuery, {
    skip: !isCurrentUser,
    variables: {
      userId: selected.id as number,
      goalDueBetweenDates: goalDueBetweenDates,
    },
  });
  const artifacts = data?.user?.favouriteArtifacts
    ? assertEdgesNonNull(data.user.favouriteArtifacts)
    : [];
  const goals = artifacts.filter(
    (artifact) => artifact.__typename === "GoalArtifactNode",
  ) as GoalOverviewGoalFragmentFragment[];

  if (!isCurrentUser) {
    return null;
  }

  return !data && loading ? (
    <Loading className="text-sm">
      Loading {label("goal", { pluralize: true })}
    </Loading>
  ) : (
    <>
      <div className="text-gray-800">
        <div className="flex justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="font-medium">{selected.name}'s Favorites</div>
            {loading && <Loading mini size={4} />}
          </div>
        </div>
        <GoalOverviewGoalTable goals={goals} />
      </div>
    </>
  );
};

export default GoalOverviewFavouriteGoals;
